import { useState, useEffect, useRef, useContext } from "react";
import AppShell, { ThemeCtx } from "./AppShell";
import { C, LOGO_BASE, ATTACH_BASE, THUMB_BASE, post } from "./utils";
import EditScreen from "./EditScreen";
import ProductProfileDetail from "./ProductProfileDetail";
import CommentsPanel from "./CommentsPanel";
import EchoPlayer, { hasEchoContent } from "./EchoPlayer";

const PHASE_CONFIG = {
  "Approved":   { color:"#059669", bg:"#D1FAE5" },
  "Phase III":  { color:"#1D4ED8", bg:"#DBEAFE" },
  "Phase II":   { color:"#7C3AED", bg:"#EDE9FE" },
  "Phase I/II": { color:"#0891B2", bg:"#CFFAFE" },
  "Phase I":    { color:"#0891B2", bg:"#CFFAFE" },
  "Phase Ib":   { color:"#0891B2", bg:"#CFFAFE" },
  "Phase Ia":   { color:"#0891B2", bg:"#CFFAFE" },
};
const phaseCfg = p => {
  if(!p) return { color:"#6B7280", bg:"#F3F4F6" };
  // exact match first
  if(PHASE_CONFIG[p]) return PHASE_CONFIG[p];
  // partial match — longest key wins to avoid "Phase I" matching "Phase III"
  const match = Object.keys(PHASE_CONFIG)
    .filter(k => p.toLowerCase().includes(k.toLowerCase()))
    .sort((a,b) => b.length - a.length)[0];
  return match ? PHASE_CONFIG[match] : { color:"#6B7280", bg:"#F3F4F6" };
};

const isImg     = p => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p||"");
const fmtDate = (d) => {
  if (!d) return null;
  const s = String(d).trim();
  if (!s) return null;
  try {
    const dt = new Date(s);
    if (isNaN(dt)) return s;
    return dt.toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });
  } catch { return s; }
};

const openFiltered = (subId, params) => {
  // Opens a new tab showing filtered list via advance_filter
  // params: { brandid, sponsorid, indicationid, congressid, phaseid, countryid, acronymsid, search }
  const base = window.location.origin + window.location.pathname;
  const qs   = new URLSearchParams({
    filter: "1",
    sub:    subId||"",
    ...params,
  }).toString();
  window.open(`${base}?${qs}`, "_blank", "noopener,noreferrer");
};

const openDocTab = (doc, subId) => {
  if(!doc?.DOCUMENTID||!doc?.DOCTYPEID) return;
  const base = window.location.origin + window.location.pathname;
  window.open(`${base}?doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};
const fileNm    = p => (p||"").split("/").pop();
const attachUrl = p => ATTACH_BASE + p;
const thumbUrl  = p => THUMB_BASE  + fileNm(p);

// ─── Shared components ────────────────────────────────────────────────────────
export function Spin() {
  const tPrimary = useContext(ThemeCtx)?.theme?.primary || C.primary;
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",gap:"8px",color:"#9CA3AF"}}>
      <div style={{width:"16px",height:"16px",border:`2px solid #E5E7EB`,borderTopColor:tPrimary,
        borderRadius:"50%",animation:"_sp 0.8s linear infinite"}}/>
      <style>{"@keyframes _sp{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

function FilePillSmall({ filepath }) {
  const nm  = fileNm(filepath).replace(/_\d{2}_\d{2}_\d{4}_\d{2}_\d{2}\./,".");
  const ext = nm.split(".").pop().toUpperCase();
  const ec  = ext==="PDF"?"#DC2626":ext==="PPTX"||ext==="PPT"?"#D97706":ext==="DOCX"?"#2563EB":"#6B7280";
  return (
    <div onClick={()=>window.open(attachUrl(filepath),"_blank","noopener,noreferrer")}
      style={{display:"inline-flex",alignItems:"center",gap:"4px",padding:"3px 9px",
        borderRadius:"7px",cursor:"pointer",background:"#fff",
        border:`1px solid ${ec}44`,fontSize:"11px",color:"#374151"}}>
      <span>📄</span>
      <span style={{maxWidth:"140px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:"500"}}>{nm}</span>
      <span style={{background:ec,color:"#fff",borderRadius:"4px",padding:"0 4px",fontSize:"8px",fontWeight:"800"}}>{ext}</span>
    </div>
  );
}

function SourceSection({ html }) {
  // Only render the label+links if there are actual links to show
  const tPrimary = useContext(ThemeCtx)?.theme?.primary || C.primary;
  if (!html||!html.trim()) return null;
  const doc     = new DOMParser().parseFromString(html,"text/html");
  const anchors = Array.from(doc.querySelectorAll("a")).filter(a=>{
    const href=a.getAttribute("href")||"";
    return href&&!href.startsWith("blob:");
  });
  const plain = decodeURIComponent(doc.body.textContent.trim());
  const hasContent = anchors.length>0 || /^https?:\/\//.test(plain);
  if (!hasContent) return null;
  return (
    <div style={{display:"flex",alignItems:"flex-start",gap:"8px",fontSize:"12px"}}>
      <span style={{color:"#9CA3AF",fontWeight:"600",minWidth:"52px",flexShrink:0}}>Source</span>
      <SourceLinks html={html}/>
    </div>
  );
}

function SourceLinks({ html }) {
  const tPrimary = useContext(ThemeCtx)?.theme?.primary || C.primary;
  if (!html) return null;
  const doc     = new DOMParser().parseFromString(html,"text/html");
  const anchors = Array.from(doc.querySelectorAll("a"));
  let links = [];
  if (anchors.length>0) {
    links = anchors.map(a=>{
      const href=a.getAttribute("href")||"";
      if(!href||href.startsWith("blob:")) return null;
      let label=a.textContent.trim();
      if(!label||label.toLowerCase()==="link"){
        let prev=a.previousSibling;
        while(prev){const t=(prev.textContent||"").trim().replace(/^[\s,()–-]+/,"").replace(/[\s,()–-]+$/,"").trim();if(t){label=t;break;}prev=prev.previousSibling;}
      }
      return {href,label:label||"Source"};
    }).filter(Boolean);
  } else {
    const plain=decodeURIComponent(doc.body.textContent.trim());
    links=plain.split(/,\s*(?=https?:\/\/)/).map((u,i)=>({href:u.trim(),label:`Source ${i+1}`})).filter(x=>/^https?:\/\//.test(x.href));
  }
  if (!links.length) return null;
  return (
    <div style={{display:"flex",flexWrap:"wrap",gap:"5px"}}>
      {links.map((lnk,i)=>(
        <a key={i} href={lnk.href} target="_blank" rel="noopener noreferrer"
          style={{display:"inline-flex",alignItems:"center",gap:"3px",padding:"2px 8px",
            borderRadius:"5px",fontSize:"11px",fontWeight:"600",color:tPrimary,
            background:`${tPrimary}10`,border:`1px solid ${tPrimary}30`,textDecoration:"none"}}>
          🔗 {lnk.label}
        </a>
      ))}
    </div>
  );
}

export function ImageStrip({ images, compact=false }) {
  const [active,setActive] = useState(0);
  const [lb,setLb]         = useState(false);
  const tPrimary = useContext(ThemeCtx)?.theme?.primary || C.primary;
  if (!images.length) return null;
  const h = compact ? 90 : 155;
  return (
    <div>
      <div onClick={()=>setLb(true)}
        style={{borderRadius:compact?"8px 8px 0 0":"8px",overflow:"hidden",background:"rgba(0,0,0,0.2)",border:"none",
          cursor:"zoom-in",marginBottom:compact?0:"6px",position:"relative",height:h,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
        <img src={attachUrl(images[active].FILEPATH)} alt="" onError={e=>e.target.style.display="none"}
          style={{maxWidth:"100%",maxHeight:`${h}px`,objectFit:"cover",width:"100%"}}/>
        {images.length>1&&<div style={{position:"absolute",bottom:"5px",right:"7px",
          background:"rgba(0,0,0,0.45)",borderRadius:"4px",padding:"1px 6px",fontSize:"9px",color:"#fff"}}>
          {active+1}/{images.length}</div>}
        {active>0&&<button onClick={e=>{e.stopPropagation();setActive(a=>a-1);}}
          style={{position:"absolute",left:"4px",width:"22px",height:"22px",borderRadius:"50%",
            border:"none",background:"rgba(0,0,0,0.4)",color:"#fff",fontSize:"13px",cursor:"pointer"}}>‹</button>}
        {active<images.length-1&&<button onClick={e=>{e.stopPropagation();setActive(a=>a+1);}}
          style={{position:"absolute",right:"4px",width:"22px",height:"22px",borderRadius:"50%",
            border:"none",background:"rgba(0,0,0,0.4)",color:"#fff",fontSize:"13px",cursor:"pointer"}}>›</button>}
      </div>
      {images.length>1&&<div style={{display:"flex",gap:"4px",overflowX:"auto",scrollbarWidth:"none"}}>
        {images.map((img,j)=>(
          <div key={j} onClick={()=>setActive(j)}
            style={{flexShrink:0,width:"38px",height:"38px",borderRadius:"5px",overflow:"hidden",
              cursor:"pointer",border:`2px solid ${active===j?tPrimary:"transparent"}`,
              opacity:active===j?1:0.45,transition:"all 0.12s"}}>
            <img src={thumbUrl(img.FILEPATH)} alt="" onError={e=>e.target.style.display="none"}
              style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          </div>
        ))}
      </div>}
      {lb&&(
        <div onClick={()=>setLb(false)} style={{position:"fixed",inset:0,zIndex:99999,
          background:"rgba(0,0,0,0.92)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          {active>0&&<button onClick={e=>{e.stopPropagation();setActive(a=>a-1);}}
            style={{position:"absolute",left:"16px",width:"40px",height:"40px",borderRadius:"50%",
              border:"none",background:"rgba(255,255,255,0.15)",color:"#fff",fontSize:"22px",cursor:"pointer"}}>‹</button>}
          <img src={attachUrl(images[active].FILEPATH)} alt="" onClick={e=>e.stopPropagation()}
            style={{maxWidth:"90vw",maxHeight:"90vh",objectFit:"contain",borderRadius:"8px"}}/>
          {active<images.length-1&&<button onClick={e=>{e.stopPropagation();setActive(a=>a+1);}}
            style={{position:"absolute",right:"16px",width:"40px",height:"40px",borderRadius:"50%",
              border:"none",background:"rgba(255,255,255,0.15)",color:"#fff",fontSize:"22px",cursor:"pointer"}}>›</button>}
          <button onClick={()=>setLb(false)} style={{position:"absolute",top:"16px",right:"16px",
            width:"36px",height:"36px",borderRadius:"50%",border:"none",
            background:"rgba(255,255,255,0.15)",color:"#fff",fontSize:"20px",cursor:"pointer"}}>×</button>
        </div>
      )}
    </div>
  );
}

// ─── Right panel (tabs) ───────────────────────────────────────────────────────
// ─── Echo/Transcript launch button ────────────────────────────────────────────
function EchoLaunchButton({ chatbot, primary, user, activeSub, documentId, docTitle }) {
  const [open, setOpen] = useState(false);
  if(!hasEchoContent(chatbot)) return null;
  return (
    <>
      <button onClick={()=>setOpen(true)}
        style={{display:"flex",alignItems:"center",gap:4,padding:"3px 9px",
          borderRadius:20,border:`1px solid ${primary}44`,
          background:`${primary}10`,color:primary,
          fontSize:11,fontWeight:600,cursor:"pointer",flexShrink:0}}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
        Transcript
      </button>
      {open&&(
        <>
          <div onMouseDown={()=>setOpen(false)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:2000}}/>
          <div onMouseDown={e=>e.stopPropagation()}
            style={{position:"fixed",
              top:"max(56px, calc(50% - min(380px, 47vh)))",
              left:"50%",transform:"translateX(-50%)",
              width:"min(1100px,97vw)",height:"min(760px,calc(100vh - 70px))",
              background:"#fff",borderRadius:14,zIndex:2001,
              boxShadow:"0 24px 70px rgba(0,0,0,0.25)",
              display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <EchoPlayer chatbot={chatbot} user={user} activeSub={activeSub}
              documentId={documentId} docTitle={docTitle} onClose={()=>setOpen(false)}/>
          </div>
        </>
      )}
    </>
  );
}

// ─── Mobile Drawer ─────────────────────────────────────────────────────────
function MobileDrawer({ files, images, chatbot, user, activeSub, documentId, docTitle, pollCount, commentCount, readOnly, scrollToComment, tPrimary }) {
  const [open, setOpen] = useState(false);
  const [tab,  setTab]  = useState(scrollToComment?"comments":files.length>0?"attachments":"comments");
  const tabs = [
    {id:"attachments", label:"Attachments", count:files.length+images.length},
    {id:"comments",    label:"Comments",    count:commentCount},
    {id:"polls",       label:"Polls",       count:pollCount},
  ];
  return (
    <>
      {/* Sticky tab bar — always visible */}
      <div style={{flexShrink:0,background:"#DFE7EC",borderTop:"1px solid #B0BEC5",
        display:"flex",zIndex:50}}>
        {tabs.map(t=>(
          <button key={t.id}
            onClick={()=>{ setTab(t.id); setOpen(true); }}
            style={{flex:1,padding:"10px 4px",border:"none",
              background:tab===t.id&&open?`${tPrimary}15`:"transparent",
              cursor:"pointer",fontSize:"11px",fontWeight:tab===t.id&&open?"700":"500",
              color:tab===t.id&&open?tPrimary:"#607D8B",
              borderTop:tab===t.id&&open?`2px solid ${tPrimary}`:"2px solid transparent",
              display:"flex",flexDirection:"column",alignItems:"center",gap:"2px"}}>
            {t.label}
            {t.count>0&&<span style={{fontSize:"9px",background:tab===t.id&&open?tPrimary:"#90A4AE",
              color:"#fff",borderRadius:"10px",padding:"0 5px",lineHeight:"14px"}}>
              {t.count}
            </span>}
          </button>
        ))}
        <button onClick={()=>setOpen(o=>!o)}
          style={{width:"36px",border:"none",background:"transparent",cursor:"pointer",
            fontSize:"16px",color:"#607D8B"}}>
          {open?"▼":"▲"}
        </button>
      </div>

      {/* Drawer panel */}
      {open&&(
        <div style={{height:"55vh",display:"flex",flexDirection:"column",
          background:"#DFE7EC",overflow:"hidden"}}>
          <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column",background:"#DFE7EC"}}>
            {tab==="attachments"&&(files.length>0
              ?<div style={{padding:"10px 12px",display:"flex",flexWrap:"wrap",gap:"6px",
                  overflowY:"auto",height:"100%",alignContent:"flex-start"}}>
                {files.map((f,i)=><FilePillSmall key={i} filepath={f.FILEPATH}/>)}
              </div>
              :<div style={{padding:"2rem",textAlign:"center",color:"#9CA3AF",fontSize:"12px"}}>No attachments</div>
            )}
            {tab==="comments"&&(
              <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
                {(readOnly&&!scrollToComment)
                  ?<div style={{display:"flex",flexDirection:"column",alignItems:"center",
                      justifyContent:"center",height:"100%",gap:"8px",color:"#9CA3AF",padding:"24px",textAlign:"center"}}>
                      <div style={{fontSize:"13px",fontWeight:"600",color:"#6B7280"}}>Comments disabled</div>
                      <div style={{fontSize:"11px",lineHeight:1.5}}>This document is inactive.</div>
                    </div>
                  :<CommentsPanel user={user} activeSub={activeSub} documentId={documentId} scrollToComment={scrollToComment}/>}
              </div>
            )}
            {tab==="polls"&&(
              <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
                <CommentsPanel user={user} activeSub={activeSub} documentId={documentId} pollOnly={true}/>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function RightPanel({ files, images, user, activeSub, documentId, chatbot=[], docTitle="", pollCount=0, commentCount=0, readOnly=false, scrollToComment=null }) {
  const [tab,setTab] = useState(scrollToComment?"comments":files.length>0?"attachments":"comments");
  const ctx      = useContext(ThemeCtx);
  const tPrimary = ctx?.theme?.primary || C.primary;
  const isMobile = ctx?.isMobile || false;
  return (
    <div style={{width:isMobile?"100%":"280px",flexShrink:0,display:"flex",flexDirection:"column",
      overflow:"hidden",background:"#DFE7EC",
      borderLeft:isMobile?"none":"1px solid #B0BEC5",
      borderTop:isMobile?"1px solid #B0BEC5":"none",
      height:isMobile?"50vh":"auto"}}>

      {/* tab strip */}
      <div style={{display:"flex",borderBottom:"1px solid #B0BEC5",
        flexShrink:0,background:"#CFD8DC",padding:"0 4px",alignItems:"center"}}>
        {[
          {id:"attachments", label:"Attachments"},
          {id:"comments",    label:"Comments"},
          {id:"polls",       label:"Polls"},
        ].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:"9px 8px",border:"none",background:"none",cursor:"pointer",
              fontSize:"11px",fontWeight:tab===t.id?"700":"500",
              color:tab===t.id?tPrimary:"#6B7280",
              borderBottom:`2px solid ${tab===t.id?tPrimary:"transparent"}`,
              transition:"all 0.15s",whiteSpace:"nowrap"}}>
            {t.label}
          </button>
        ))}
        <div style={{flex:1}}/>
        <div style={{paddingRight:6}}>
          <EchoLaunchButton chatbot={chatbot} primary={tPrimary}
            user={user} activeSub={activeSub} documentId={documentId} docTitle={docTitle}/>
        </div>
      </div>

      <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column",background:"#DFE7EC"}}>
        {tab==="attachments"&&(files.length>0
          ?<div style={{padding:"10px 12px",display:"flex",flexWrap:"wrap",gap:"6px",
              overflowY:"auto",height:"100%",alignContent:"flex-start"}}>
            {files.map((f,i)=><FilePillSmall key={i} filepath={f.FILEPATH}/>)}
          </div>
          :<div style={{padding:"2rem",textAlign:"center",color:"#9CA3AF",fontSize:"12px"}}>No attachments</div>
        )}
        {tab==="comments"&&(
          <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}} ref={el=>{
            if(el&&scrollToComment){
              const id="msg-"+scrollToComment;
              const t=el.querySelector("#"+id);
              if(t)setTimeout(()=>t.scrollIntoView({behavior:"smooth",block:"center"}),300);
            }
          }}>
            {(readOnly&&!scrollToComment)
            ? <div style={{display:"flex",flexDirection:"column",alignItems:"center",
                justifyContent:"center",height:"100%",gap:"8px",color:"#9CA3AF",padding:"24px",textAlign:"center"}}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity=".4"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
                <div style={{fontSize:"13px",fontWeight:"600",color:"#6B7280"}}>Comments disabled</div>
                <div style={{fontSize:"11px",lineHeight:1.5}}>This document is inactive.<br/>Comments are view-only.</div>
              </div>
            : <CommentsPanel user={user} activeSub={activeSub} documentId={documentId} scrollToComment={scrollToComment}/>}
          </div>
        )}
        {tab==="polls"&&(
          <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <CommentsPanel user={user} activeSub={activeSub} documentId={documentId} pollOnly={true}/>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
export function Hero({ d, tPrimary, images=[] }) {
  const companies   = (Array.isArray(d.COMPANY)?d.COMPANY:[]).filter(c=>String(c.SPONSORID)!=="0");
  const products    = (Array.isArray(d.PRODUCTS)?d.PRODUCTS:[]).filter(p=>String(p.BRANDID)!=="0");
  const indications = (Array.isArray(d.INDICATIONS)?d.INDICATIONS:[]).filter(i=>String(i.INDICATIONID)!=="0");
  const chatbot     = Array.isArray(d.CHATBOT)?d.CHATBOT:[];
  const thumb       = chatbot.filter(a=>isImg(a.FILEPATH)).sort((a,b)=>b.ATTACHMENTID-a.ATTACHMENTID)[0]||null;
  const primaryCo   = companies.find(c=>c.PRIMARY===1)||companies[0]||null;
  const primaryProd = products.find(p=>p.PRIMARY===1)||null;

  return (
    <div style={{flexShrink:0,padding:"16px 20px",
      background:`linear-gradient(135deg,${tPrimary}F0,${tPrimary}88)`,
      position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",right:"-20px",top:"-20px",width:"130px",height:"130px",
        borderRadius:"50%",background:"rgba(255,255,255,0.05)"}}/>
      <div style={{display:"flex",gap:"12px",alignItems:"flex-start",position:"relative"}}>
        {primaryCo?.COMPANY_LOGO&&<div style={{width:"44px",height:"44px",borderRadius:"10px",flexShrink:0,
          background:"rgba(255,255,255,0.18)",border:"1.5px solid rgba(255,255,255,0.3)",
          overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <img src={LOGO_BASE+primaryCo.COMPANY_LOGO} alt="" onError={e=>e.target.style.display="none"}
            style={{width:"80%",height:"80%",objectFit:"contain"}}/>
        </div>}
        <div style={{flex:1,minWidth:0}}>
          {/* Doctype + Congress badges */}
          <div style={{display:"flex",flexWrap:"wrap",gap:"4px",marginBottom:"4px",alignItems:"center"}}>
            {d.DOCTYPENAME&&<span style={{display:"inline-flex",background:"rgba(255,255,255,0.2)",
              borderRadius:"20px",padding:"1px 8px",fontSize:"9px",fontWeight:"700",
              color:"rgba(255,255,255,0.9)",textTransform:"uppercase",letterSpacing:"0.6px"}}>
              {d.DOCTYPENAME}</span>}
            {d.CONGRESSNAME&&(
              <span onClick={()=>d.CONGRESSID&&openFiltered(d.SUBPROJECTID||new URLSearchParams(window.location.search).get("sub"),{congressid:d.CONGRESSID,congressname:d.CONGRESSNAME||"",stateid:2})}
                style={{display:"inline-flex",alignItems:"center",gap:"5px",
                  background:"rgba(255,255,255,0.15)",borderRadius:"20px",
                  padding:"3px 10px 3px 6px",fontSize:"9px",fontWeight:"600",
                  color:"rgba(255,255,255,0.92)",
                  cursor:d.CONGRESSID?"pointer":"default",
                  border:"1px solid rgba(255,255,255,0.2)",
                  transition:"background .15s"}}
                onMouseEnter={e=>{ if(d.CONGRESSID) e.currentTarget.style.background="rgba(255,255,255,0.28)"; }}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.15)"}>
                {/* Congress SVG icon — podium with network nodes */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="rgba(255,255,255,0.95)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  {/* podium */}
                  <rect x="9" y="13" width="6" height="5" rx="1"/>
                  <path d="M7 18h10"/>
                  {/* microphone stand */}
                  <line x1="12" y1="10" x2="12" y2="13"/>
                  <ellipse cx="12" cy="8.5" rx="2" ry="2.5"/>
                  {/* audience arcs */}
                  <path d="M5 21c0-3.866 3.134-7 7-7s7 3.134 7 7" opacity="0.5"/>
                  <path d="M2 21c0-5.523 4.477-10 10-10s10 4.477 10 10" opacity="0.3"/>
                  {/* network dots */}
                  <circle cx="4" cy="9" r="1.2" fill="rgba(255,255,255,0.7)" stroke="none"/>
                  <circle cx="20" cy="9" r="1.2" fill="rgba(255,255,255,0.7)" stroke="none"/>
                  <circle cx="12" cy="3" r="1.2" fill="rgba(255,255,255,0.7)" stroke="none"/>
                  <line x1="4" y1="9" x2="12" y2="3" opacity="0.5"/>
                  <line x1="20" y1="9" x2="12" y2="3" opacity="0.5"/>
                  <line x1="4" y1="9" x2="20" y2="9" opacity="0.3"/>
                </svg>
                {d.CONGRESSNAME}
              </span>
            )}
          </div>
          {/* Title */}
          <div style={{fontSize:"17px",fontWeight:"800",color:"#fff",lineHeight:1.25,marginBottom:"6px"}}>
            {primaryProd?.BRANDNAME&&<span style={{opacity:0.85}}>{primaryProd.BRANDNAME}: </span>}
            {d.SUBJECT}
          </div>
          {/* All pills in one row — company, product, indication, hashtags */}
          {(companies.length>0||products.length>0||indications.length>0||(Array.isArray(d.HASHTAGS)&&d.HASHTAGS.filter(Boolean).length>0)||d.KEYWORDS)&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:"4px",marginBottom:"5px",alignItems:"center"}}>
              {companies.map(c=>(
                <span key={c.SPONSORID}
                  onClick={()=>openFiltered(d.SUBPROJECTID||new URLSearchParams(window.location.search).get("sub"),{sponsorid:c.SPONSORID,sponsorname:c.SPONSORNAME?.trim(),stateid:2})}
                  title={`Filter by ${c.SPONSORNAME?.trim()}`}
                  style={{fontSize:"11px",fontWeight:"600",cursor:"pointer",
                    padding:"2px 9px",borderRadius:"20px",userSelect:"none",
                    background:c.PRIMARY===1?"rgba(255,255,255,0.92)":"rgba(255,255,255,0.12)",
                    color:c.PRIMARY===1?tPrimary:"rgba(255,255,255,0.75)",
                    border:`1px solid ${c.PRIMARY===1?"rgba(255,255,255,0.9)":"rgba(255,255,255,0.2)"}`,
                    transition:"all 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.opacity="0.75"}
                  onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                  {c.SPONSORNAME?.trim()}
                </span>
              ))}
              {products.map(p=>(
                <span key={p.BRANDID}
                  onClick={()=>openFiltered(d.SUBPROJECTID||new URLSearchParams(window.location.search).get("sub"),{brandid:p.BRANDID,brandname:p.BRANDNAME,stateid:2})}
                  title={`Filter by ${p.BRANDNAME}`}
                  style={{fontSize:"10px",fontWeight:"600",cursor:"pointer",
                    padding:"2px 9px",borderRadius:"20px",userSelect:"none",
                    background:p.PRIMARY===1?"#FCD34D":"rgba(252,211,77,0.15)",
                    color:p.PRIMARY===1?"#78350F":"rgba(252,211,77,0.85)",
                    border:`1px solid ${p.PRIMARY===1?"#FCD34D":"rgba(252,211,77,0.3)"}`,
                    transition:"all 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.opacity="0.75"}
                  onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                  {p.BRANDNAME}
                </span>
              ))}
              {indications.map(ind=>(
                <span key={ind.INDICATIONID}
                  onClick={()=>openFiltered(d.SUBPROJECTID||new URLSearchParams(window.location.search).get("sub"),{indicationid:ind.INDICATIONID,indicationname:ind.INDICATIONNAME,stateid:2})}
                  title={`Filter by ${ind.INDICATIONNAME}`}
                  style={{fontSize:"10px",fontWeight:"500",cursor:"pointer",
                    padding:"2px 9px",borderRadius:"20px",userSelect:"none",
                    background:ind.PRIMARY===1?"rgba(167,243,208,0.9)":"rgba(167,243,208,0.12)",
                    color:ind.PRIMARY===1?"#065F46":"rgba(167,243,208,0.85)",
                    border:`1px solid ${ind.PRIMARY===1?"rgba(167,243,208,0.8)":"rgba(167,243,208,0.25)"}`,
                    transition:"all 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.opacity="0.75"}
                  onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                  {ind.INDICATIONNAME}
                </span>
              ))}
              {(()=>{
                const tags=[
                  ...(Array.isArray(d.HASHTAGS)?d.HASHTAGS:[]),
                  ...((d.KEYWORDS||"").split(",").map(s=>s.trim()).filter(Boolean))
                ].filter((t,i,a)=>t&&a.indexOf(t)===i); // unique
                return tags.map((tag,i)=>(
                  <span key={i} style={{fontSize:"9px",fontWeight:"600",
                    padding:"2px 8px",borderRadius:"20px",
                    background:"rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.85)",
                    border:"1px solid rgba(255,255,255,0.25)"}}>
                    {tag}
                  </span>
                ));
              })()}
            </div>
          )}
          {/* Dates + Phase + Acronym */}
          <div style={{display:"flex",gap:"5px",flexWrap:"wrap",alignItems:"center"}}>
            {d.DISPLAY_TIME&&<span style={{fontSize:"10px",color:"rgba(255,255,255,0.7)",
              background:"rgba(255,255,255,0.15)",borderRadius:"5px",padding:"1px 7px"}}>
              📅 {d.DISPLAY_TIME}</span>}
            {(d.STARTDATE||d.ENDDATE)&&<span style={{fontSize:"10px",color:"rgba(255,255,255,0.8)",
              fontWeight:"600",background:"rgba(255,255,255,0.12)",borderRadius:"5px",padding:"1px 7px"}}>
              {[d.STARTDATE&&`▶ ${fmtDate(d.STARTDATE)}`,d.ENDDATE&&`■ ${fmtDate(d.ENDDATE)}`].filter(Boolean).join(" → ")}
            </span>}
            {(d.PHASE||d.PHASENAME)&&<span style={{fontSize:"10px",fontWeight:"700",padding:"2px 8px",
              borderRadius:"5px",background:"rgba(255,255,255,0.2)",color:"#fff"}}>{d.PHASE||d.PHASENAME}</span>}
            {d.ACRONYMS&&<span style={{fontSize:"10px",fontWeight:"800",color:"#FCD34D",
              background:"rgba(0,0,0,0.2)",borderRadius:"5px",padding:"1px 8px"}}>{d.ACRONYMS}</span>}
            {d.ACRONYMS_NAME?.ACRONYMSID&&d.ACRONYMS_NAME?.ACRONYMS&&(
              <span onClick={()=>{
                  const sub=d.SUBPROJECTID||new URLSearchParams(window.location.search).get("sub")||"";
                  window.open(`${window.location.origin+window.location.pathname}?fdetail=1&doc=${d.ACRONYMS_NAME.ACRONYMSID}&dt=96&sub=${sub}`,"_blank","noopener,noreferrer");
                }}
                title="View Clinical Trial"
                style={{fontSize:"10px",fontWeight:"800",color:"#FCD34D",
                  background:"rgba(0,0,0,0.2)",borderRadius:"5px",padding:"1px 8px",
                  cursor:"pointer",display:"inline-flex",alignItems:"center",gap:"3px"}}
                onMouseEnter={e=>e.currentTarget.style.opacity="0.75"}
                onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M6 2h12M8 2v7l-5 9a2 2 0 001.76 2.96h10.48A2 2 0 0017 18L12 9V2"/>
                  <path d="M6 13h12"/>
                </svg>
                {d.ACRONYMS_NAME.ACRONYMS}
              </span>
            )}
          </div>
        </div>
        {images.length>0&&<div style={{width:"90px",flexShrink:0,borderRadius:"10px",overflow:"hidden",
          border:"2px solid rgba(255,255,255,0.25)"}}>
          <ImageStrip images={images} compact={true}/>
        </div>}
      </div>
      {d.FIRSTNAME&&<div style={{position:"absolute",bottom:"6px",right:"12px",
        fontSize:"9px",color:"rgba(255,255,255,0.5)",fontStyle:"italic"}}>
        by {d.FIRSTNAME}
      </div>}

    </div>
  );
}

// ─── Inline Edit Form ────────────────────────────────────────────────────────
export function InlineEdit({ doc, user, activeSub, tPrimary, onDone, onCancel }) {
  // sort attachments desc before passing to EditScreen
  const sortedDoc = {
    ...doc,
    CHATBOT: Array.isArray(doc.CHATBOT)
      ? [...doc.CHATBOT].sort((a,b)=>b.ATTACHMENTID-a.ATTACHMENTID)
      : []
  };
  return (
    <EditScreen
      doc={sortedDoc} user={user} activeSub={activeSub}
      onDone={onDone} onCancel={onCancel}/>
  );
}


// ─── Shared admin action bar ─────────────────────────────────────────────────
// ─── Share modal ──────────────────────────────────────────────────────────────
function ShareModal({ doc, user, activeSub, tPrimary, onClose }) {
  const [tab,      setTab]      = useState("email");
  // email tab
  const [input,    setInput]    = useState("");
  const [emails,   setEmails]   = useState([]);
  const [sending,  setSending]  = useState(false);
  const [result,   setResult]   = useState(null);
  // dm tab
  const [groups,     setGroups]     = useState([]);
  const [grpLoading, setGrpLoading] = useState(false);
  const [selGroup,   setSelGroup]   = useState(null);
  const [dmMessage,  setDmMessage]  = useState("");
  const [dmSending,  setDmSending]  = useState(false);
  const [dmResult,   setDmResult]   = useState(null);

  // load DM groups when tab switches to dm
  useEffect(()=>{
    if(tab!=="dm"||groups.length>0) return;
    setGrpLoading(true);
    fetch(`https://cip.medsphere.net/api/dm_history?userid=${user.USERID}&subprojectid=${activeSub?.SUBPROJECTID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json())
      .then(res=>{ if(res.STATUS==="1"&&Array.isArray(res.DATA)) setGroups(res.DATA); })
      .catch(()=>{})
      .finally(()=>setGrpLoading(false));
  },[tab]);

  // email helpers
  const addEmail = (raw) => {
    const vals = raw.split(/[,\s]+/).map(e=>e.trim().toLowerCase()).filter(e=>e.includes("@"));
    if(!vals.length) return;
    setEmails(prev=>[...new Set([...prev,...vals])]);
    setInput("");
  };
  const handleKeyDown = (e) => {
    if(e.key==="Enter"||e.key===",") { e.preventDefault(); addEmail(input); }
  };

  const sendEmail = async () => {
    if(!emails.length) return;
    setSending(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("documentid", doc.DOCUMENTID);
      fd.append("userid",     user.USERID);
      emails.forEach(e=>fd.append("email[]",e));
      const r   = await fetch("https://cip.medsphere.net/api/share_doc",
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res = await r.json();
      if(res.STATUS==="1"||res.STATUS===1) {
        setResult({ok:true,msg:`Shared with ${emails.length} recipient${emails.length!==1?"s":""}`});
        setEmails([]); setInput("");
      } else {
        setResult({ok:false,msg:res.MESSAGE||res.message||"Share failed."});
      }
    } catch { setResult({ok:false,msg:"Network error."}); }
    setSending(false);
  };

  const sendDM = async () => {
    if(!selGroup) return;
    setDmSending(true); setDmResult(null);
    try {
      const fd = new FormData();
      fd.append("sender",     user.USERID);
      fd.append("groupid",    selGroup.GROUPID||selGroup.DMID);
      fd.append("privacy",    0);
      fd.append("type",       "text");
      fd.append("message",    dmMessage.trim() || "");
      fd.append("documentid", doc.DOCUMENTID);
      const r   = await fetch("https://cip.medsphere.net/api/D_Message",
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res = await r.json();
      if(res.STATUS==="1"||res.STATUS===1) {
        setDmResult({ok:true,msg:`Sent to ${selGroup.GROUPNAME}`});
        setSelGroup(null); setDmMessage("");
      } else {
        setDmResult({ok:false,msg:res.MESSAGE||res.message||"Send failed."});
      }
    } catch { setDmResult({ok:false,msg:"Network error."}); }
    setDmSending(false);
  };

  return (
    <>
      <div onMouseDown={onClose}
        style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:2000}}/>
      <div onMouseDown={e=>e.stopPropagation()}
        style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
          width:"min(480px,92vw)",background:"#fff",borderRadius:12,
          boxShadow:"0 20px 60px rgba(0,0,0,0.2)",zIndex:2001,overflow:"hidden"}}>

        {/* header */}
        <div style={{padding:"14px 18px",borderBottom:"1px solid #E5E7EB",
          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:15,fontWeight:700,color:"#111"}}>Share Document</div>
          <button onClick={onClose}
            style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:"#9CA3AF",padding:0}}>×</button>
        </div>

        {/* tabs */}
        <div style={{display:"flex",borderBottom:"1px solid #E5E7EB"}}>
          {[{id:"email",label:"📧 Email"},{id:"dm",label:"💬 DM Group"}].map(t=>(
            <button key={t.id} onClick={()=>{setTab(t.id);setResult(null);setDmResult(null);}}
              style={{flex:1,padding:"9px",border:"none",background:"none",cursor:"pointer",
                fontSize:13,fontWeight:tab===t.id?700:500,
                color:tab===t.id?tPrimary:"#6B7280",
                borderBottom:`2px solid ${tab===t.id?tPrimary:"transparent"}`,
                transition:"all .12s"}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* body */}
        <div style={{padding:"16px 18px",minHeight:160}}>

          {/* email tab */}
          {tab==="email" && (
            <>
              <div style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 8px",
                minHeight:44,display:"flex",flexWrap:"wrap",gap:5,alignItems:"center",
                background:"#fff",cursor:"text"}}
                onClick={()=>document.getElementById("shareEmailInput")?.focus()}>
                {emails.map(e=>(
                  <span key={e} style={{display:"inline-flex",alignItems:"center",gap:4,
                    background:`${tPrimary}15`,color:tPrimary,borderRadius:20,
                    padding:"2px 8px",fontSize:12,fontWeight:600}}>
                    {e}
                    <span onClick={()=>setEmails(p=>p.filter(x=>x!==e))}
                      style={{cursor:"pointer",fontSize:14,lineHeight:1,opacity:0.7}}>×</span>
                  </span>
                ))}
                <input id="shareEmailInput" value={input}
                  onChange={e=>setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={()=>input.trim()&&addEmail(input)}
                  placeholder={emails.length?"":"Enter emails, comma or Enter to add…"}
                  style={{border:"none",outline:"none",fontSize:13,flex:1,minWidth:120,
                    background:"transparent",fontFamily:"inherit",color:"#374151"}}/>
              </div>
              <div style={{fontSize:11,color:"#9CA3AF",marginTop:5}}>
                Separate multiple emails with comma or press Enter
              </div>
              {result&&(
                <div style={{marginTop:10,padding:"8px 12px",borderRadius:7,fontSize:12,
                  background:result.ok?"#F0FDF4":"#FEF2F2",
                  color:result.ok?"#16A34A":"#DC2626",
                  border:`1px solid ${result.ok?"#BBF7D0":"#FECACA"}`}}>
                  {result.msg}
                </div>
              )}
            </>
          )}

          {/* dm tab */}
          {tab==="dm" && (
            <>
              {grpLoading && (
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",
                  padding:"32px 0",gap:8,color:"#9CA3AF",fontSize:13}}>
                  <div style={{width:14,height:14,border:`2px solid #E5E7EB`,
                    borderTopColor:tPrimary,borderRadius:"50%",
                    animation:"_sp 0.8s linear infinite"}}/>
                  Loading groups…
                </div>
              )}
              {!grpLoading && groups.length===0 && (
                <div style={{textAlign:"center",padding:"32px 0",color:"#9CA3AF",fontSize:13}}>
                  No DM groups found
                </div>
              )}
              {!grpLoading && groups.length>0 && (
                <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:220,overflowY:"auto"}}>
                  {groups.map(g=>{
                    const gid  = g.GROUPID||g.DMID;
                    const sel  = selGroup && (selGroup.GROUPID||selGroup.DMID)===gid;
                    const unread = Number(g["UNREAD/DM"]||g.UNREAD||0);
                    return (
                      <div key={gid} onClick={()=>setSelGroup(sel?null:g)}
                        style={{display:"flex",alignItems:"center",gap:10,
                          padding:"9px 12px",borderRadius:8,cursor:"pointer",
                          border:`1.5px solid ${sel?tPrimary:C.border}`,
                          background:sel?`${tPrimary}08`:"#fff",
                          transition:"all .12s"}}>
                        {/* avatar */}
                        <div style={{width:34,height:34,borderRadius:"50%",flexShrink:0,
                          background:`${tPrimary}20`,display:"flex",alignItems:"center",
                          justifyContent:"center",fontSize:13,fontWeight:700,color:tPrimary}}>
                          {(g.GROUPNAME||"?")[0].toUpperCase()}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:sel?700:500,color:"#111",
                            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                            {g.GROUPNAME}
                          </div>
                          {g.MESSAGE&&(
                            <div style={{fontSize:11,color:"#9CA3AF",
                              whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                              {g.MESSAGE}
                            </div>
                          )}
                        </div>
                        {unread>0&&(
                          <span style={{fontSize:10,fontWeight:700,background:tPrimary,
                            color:"#fff",borderRadius:10,padding:"1px 6px",flexShrink:0}}>
                            {unread}
                          </span>
                        )}
                        {sel&&(
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke={tPrimary} strokeWidth="2.5" strokeLinecap="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {dmResult&&(
                <div style={{marginTop:10,padding:"8px 12px",borderRadius:7,fontSize:12,
                  background:dmResult.ok?"#F0FDF4":"#FEF2F2",
                  color:dmResult.ok?"#16A34A":"#DC2626",
                  border:`1px solid ${dmResult.ok?"#BBF7D0":"#FECACA"}`}}>
                  {dmResult.msg}
                </div>
              )}

              {/* doc card preview + optional message — always visible */}
              <div style={{marginTop:12,borderTop:`1px solid ${C.border}`,paddingTop:10}}>
                {/* doc card */}
                <div style={{display:"flex",gap:8,alignItems:"flex-start",
                  background:"#F8FAFC",border:`1px solid ${C.border}`,
                  borderRadius:8,padding:"8px 10px",marginBottom:8}}>
                  <span style={{fontSize:18,flexShrink:0}}>📄</span>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#111",
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {doc.SUBJECT||"Document"}
                    </div>
                    {doc.DOCTYPENAME&&(
                      <div style={{fontSize:10,color:"#9CA3AF",marginTop:1}}>
                        {doc.DOCTYPENAME}
                      </div>
                    )}
                  </div>
                </div>
                {/* optional message */}
                <textarea value={dmMessage} onChange={e=>setDmMessage(e.target.value)}
                  placeholder="Add a message… (optional)"
                  rows={2}
                  style={{width:"100%",boxSizing:"border-box",padding:"7px 10px",
                    borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,
                    fontFamily:"inherit",color:"#374151",resize:"none",
                    outline:"none",background:"#fff"}}/>
              </div>
            </>
          )}
        </div>

        {/* footer */}
        <div style={{padding:"12px 18px",borderTop:"1px solid #E5E7EB",
          display:"flex",gap:8,justifyContent:"flex-end"}}>
          <button onClick={onClose}
            style={{padding:"7px 18px",borderRadius:7,border:`1px solid ${C.border}`,
              background:"#F7F9FC",color:C.muted,fontSize:13,cursor:"pointer",fontWeight:500}}>
            Cancel
          </button>
          {tab==="email"&&(
            <button onClick={sendEmail} disabled={!emails.length||sending}
              style={{padding:"7px 22px",borderRadius:7,border:"none",
                background:(!emails.length||sending)?"#aaa":tPrimary,
                color:"#fff",fontSize:13,fontWeight:600,cursor:sending?"wait":"pointer"}}>
              {sending?"Sending…":"Send"}
            </button>
          )}
          {tab==="dm"&&(
            <button onClick={sendDM} disabled={!selGroup||dmSending}
              style={{padding:"7px 22px",borderRadius:7,border:"none",
                background:(!selGroup||dmSending)?"#aaa":tPrimary,
                color:"#fff",fontSize:13,fontWeight:600,cursor:dmSending?"wait":"pointer"}}>
              {dmSending?"Sending…":"Send to Group"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Fav / Like / Share bar ───────────────────────────────────────────────────
function FavLikeShareBar({ doc, user, activeSub, tPrimary }) {
  const [fav,       setFav]       = useState(doc.ISFAV===1||doc.ISFAV==="1");
  const [liked,     setLiked]     = useState(doc.ISLIKE===1||doc.ISLIKE==="1");
  const [likeCount, setLikeCount] = useState(Number(doc.LIKECOUNT)||0);
  const [shareOpen, setShareOpen] = useState(false);
  const [busy,      setBusy]      = useState(false);

  const callFavLike = async (params) => {
    if(!user||!activeSub) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("userid",     user.USERID);
      fd.append("documentid", doc.DOCUMENTID);
      Object.entries(params).forEach(([k,v])=>fd.append(k,v));
      await fetch("https://cip.medsphere.net/api/favlike",
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
    } catch{}
    setBusy(false);
  };

  const toggleFav  = () => { const n=!fav;  setFav(n);  callFavLike({fav:n?1:2}); };
  const toggleLike = () => { const n=!liked; setLiked(n); setLikeCount(c=>n?c+1:Math.max(0,c-1)); callFavLike({like:n?1:2}); };

  const iconBtn = (onClick, active, activeColor, icon, label) => (
    <button onClick={onClick} disabled={busy}
      style={{display:"flex",alignItems:"center",gap:5,padding:"4px 11px",
        borderRadius:20,cursor:"pointer",transition:"all .15s",
        border:`1px solid ${active ? activeColor : C.border}`,
        background: active ? `${activeColor}12` : "transparent",
        color: active ? activeColor : C.muted,
        fontSize:11, fontWeight: active?700:500 }}>
      {icon}
      <span>{label}</span>
    </button>
  );

  // Heart SVG
  const HeartIcon = ({filled,color}) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill={filled?color:"none"}
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
    </svg>
  );

  // Thumbs up SVG
  const ThumbIcon = ({filled,color}) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill={filled?color:"none"}
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/>
      <path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/>
    </svg>
  );

  // Share SVG
  const ShareIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  );

  return (
    <>
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        {iconBtn(toggleFav, fav, "#EF4444",
          <HeartIcon filled={fav} color={fav?"#EF4444":C.muted}/>,
          fav ? "Saved" : "Save")}
        {iconBtn(toggleLike, liked, tPrimary,
          <ThumbIcon filled={liked} color={liked?tPrimary:C.muted}/>,
          likeCount > 0 ? likeCount : "Like")}
        <button onClick={()=>setShareOpen(true)}
          style={{display:"flex",alignItems:"center",gap:5,padding:"4px 11px",
            borderRadius:20,border:`1px solid ${C.border}`,background:"transparent",
            color:C.muted,fontSize:11,fontWeight:500,cursor:"pointer",transition:"all .15s"}}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=tPrimary;e.currentTarget.style.color=tPrimary;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.muted;}}>
          <ShareIcon/> Share
        </button>
      </div>
      {shareOpen&&(
        <ShareModal doc={doc} user={user} activeSub={activeSub}
          tPrimary={tPrimary} onClose={()=>setShareOpen(false)}/>
      )}
    </>
  );
}

// ─── Admin bar ────────────────────────────────────────────────────────────────
export function AdminBar({ doc, user, activeSub, tPrimary, onEdit }) {
  const isAdmin   = activeSub?.ROLE===1;
  const isMobile  = useContext(ThemeCtx)?.isMobile || false;
  const [stateid, setStateid]   = useState(doc.STATEID||doc.STATE_ID||2);
  const [loading, setLoading]   = useState(false);
  const isActive  = stateid===2||stateid==="2";

  const toggle = async () => {
    if(!user||!activeSub||!doc.DOCUMENTID) return;
    setLoading(true);
    try {
      const fd=new FormData();
      fd.append("userid",      user.USERID);
      fd.append("subprojectid",activeSub.SUBPROJECTID);
      fd.append("documentid",  doc.DOCUMENTID);
      fd.append("stateid",     isActive?1:2);
      const r   = await fetch("https://cip.medsphere.net/api/act_deact",
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res = await r.json();
      if(res.STATUS==="1") setStateid(isActive?1:2);
    } catch(e){}
    setLoading(false);
  };

  return (
    <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:"8px",
      padding:"6px 16px",background:"rgba(0,0,0,0.05)",borderBottom:"1px solid rgba(0,0,0,0.06)",
      flexWrap:"wrap"}}>
      {isMobile&&(
        <button onClick={()=>window.history.back()}
          style={{display:"flex",alignItems:"center",gap:"4px",padding:"4px 10px",
            borderRadius:"20px",border:`1px solid ${tPrimary}44`,background:`${tPrimary}10`,
            color:tPrimary,fontSize:"12px",fontWeight:"700",cursor:"pointer",flexShrink:0}}>
          ‹ Back
        </button>
      )}
      {isAdmin&&(
        <button onClick={toggle}
          title={isActive?"Visible to all users — click to deactivate":"Hidden from users — click to activate"}
          style={{display:"flex",alignItems:"center",gap:"5px",padding:"4px 12px",
            borderRadius:"20px",border:"none",cursor:"pointer",fontSize:"11px",fontWeight:"700",
            background:isActive?"#D1FAE5":"#F3F4F6",
            color:isActive?"#065F46":"#6B7280",transition:"all 0.15s"}}>
          <span style={{width:"7px",height:"7px",borderRadius:"50%",flexShrink:0,
            background:isActive?"#059669":"#9CA3AF"}}/>
          {loading?"…":isActive?"Active":"Inactive"}
        </button>
      )}
      <FavLikeShareBar doc={doc} user={user} activeSub={activeSub} tPrimary={tPrimary}/>
      <div style={{flex:1}}/>
      {isAdmin&&(
        <button onClick={onEdit}
          style={{display:"flex",alignItems:"center",gap:"5px",padding:"4px 14px",
            borderRadius:"20px",border:"1px solid #E5E7EB",cursor:"pointer",
            fontSize:"11px",fontWeight:"700",background:"#fff",
            color:"#374151",transition:"all 0.15s"}}>
          ✏ Edit
        </button>
      )}
    </div>
  );
}

// ─── Generic detail view (works for all doctypes via advance_filter) ──────────
export function GenericDetailView({ d, user, activeSub, readOnly=false, scrollToComment=null }) {
  const ctx      = useContext(ThemeCtx);
  const tPrimary = ctx?.theme?.primary || C.primary;
  const isMobile = ctx?.isMobile || false;
  const [editMode, setEditMode] = useState(false);
  const [docData,  setDocData]  = useState(d);

  const reloadDoc = () => {
    if(!user||!activeSub||!d.DOCUMENTID) return;
    fetch(`https://cip.medsphere.net/api/advance_filter?subprojectid=${activeSub.SUBPROJECTID}&userid=${user.USERID}&documentid=${d.DOCUMENTID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json())
      .then(r=>{ if(r.STATUS==="1"&&r.DATA?.length) setDocData(r.DATA[0]); })
      .catch(()=>{});
  };
  const chatbot  = Array.isArray(docData.CHATBOT)?docData.CHATBOT:[];
  const images   = chatbot.filter(a=>isImg(a.FILEPATH)).sort((a,b)=>a.ATTACHMENTID-b.ATTACHMENTID);
  const files    = chatbot.filter(a=>!isImg(a.FILEPATH)).sort((a,b)=>b.ATTACHMENTID-a.ATTACHMENTID);

  const metaFields = [
    {label:"Trial Status",    val:docData.TRIALSTATUSNAME},
    ...((docData.DOCTYPEABBREV||"").toUpperCase()==="CT"||(docData.DOCTYPENAME||"").toLowerCase().includes("clinical")?[
      {label:"Target", val:docData.TARGETN>0?String(docData.TARGETN):null},
      {label:"Actual", val:docData.CURRENTN>0?String(docData.CURRENTN):null},
    ]:[]),
    ...(docData.DOCTYPEID===98?[]:[
      {label:"Market",   val:docData.COUNTRYNAME},
      {label:"Location", val:docData.LOCATION},
    ]),
    ...(docData.DOCTYPEID!==98?[]:[
      {label:"Location", val:docData.LOCATION},
    ]),
  ].filter(r=>r.val);

  if(editMode) return (
    <InlineEdit
      doc={docData} user={user} activeSub={activeSub} tPrimary={tPrimary}
      onDone={()=>{ setEditMode(false); reloadDoc(); }}
      onCancel={()=>setEditMode(false)}/>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <Hero d={docData} tPrimary={tPrimary} images={images}/>
      <AdminBar doc={docData} user={user} activeSub={activeSub} tPrimary={tPrimary}
        onEdit={()=>setEditMode(true)}/>
      <div style={{flex:1,display:"flex",overflow:"hidden",minHeight:0,
        flexDirection:isMobile?"column":"row"}}>
        <div style={{flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:"12px"}}>

          {/* Indications & details — only for Product Details (dt=98) */}
          {docData.DOCTYPEID===98&&Array.isArray(docData.INDICATIONS)&&docData.INDICATIONS.length>0&&(()=>{
            const hasAcronym = docData.INDICATIONS.some((_,i)=>{
              const a = Array.isArray(docData.ACRONYMS_NAME)
                ? docData.ACRONYMS_NAME[i]?.ACRONYMS
                : docData.ACRONYMS_NAME?.ACRONYMS||docData.ACRONYMS||"";
              return !!a;
            });
            return (
            <div style={{background:"#fff",borderRadius:"12px",
              border:"1px solid #E5E7EB",overflow:"hidden",
              boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
              <div style={{padding:"9px 14px",borderBottom:"1px solid #F1F5F9",background:"#F8FAFC"}}>
                <span style={{fontSize:"12px",fontWeight:"700",color:"#374151"}}>Indications & details</span>
              </div>
              {/* Column headers */}
              <div style={{display:"grid",
                gridTemplateColumns:hasAcronym?"92px 1fr 120px":"92px 1fr",
                gap:"6px",padding:"5px 14px",background:"#FAFAFA",
                borderBottom:"1px solid #F1F5F9",fontSize:"10px",
                fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",letterSpacing:"0.5px"}}>
                <span>Phase</span><span>Indication</span>
                {hasAcronym&&<span>Acronym</span>}
              </div>
              {docData.INDICATIONS.map((ind,i)=>{
                const phase   = docData.PHASE||docData.PHASENAME||"";
                const pc      = phaseCfg(phase);
                const acronym = Array.isArray(docData.ACRONYMS_NAME)
                  ? docData.ACRONYMS_NAME[i]?.ACRONYMS
                  : docData.ACRONYMS_NAME?.ACRONYMS||docData.ACRONYMS||"";
                return (
                  <div key={i} style={{borderBottom:"1px solid #F1F5F9"}}>
                    <div style={{display:"grid",
                      gridTemplateColumns:hasAcronym?"92px 1fr 120px":"92px 1fr",
                      gap:"6px",alignItems:"center",padding:"9px 14px",
                      background:`${pc.color}08`}}>
                      <span style={{fontSize:"10px",fontWeight:"700",padding:"2px 7px",
                        borderRadius:"5px",color:pc.color,background:pc.bg,
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                        display:"inline-block"}}>{phase||"—"}</span>
                      <span style={{fontSize:"12px",fontWeight:"600",color:"#111827",
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {ind.INDICATIONNAME||"—"}
                      </span>
                      {hasAcronym&&<span style={{fontSize:"11px",color:tPrimary,
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {acronym||""}
                      </span>}
                    </div>
                    {(docData.LAUNCHDATE||docData.LAUNCHASSUMPTIONS||docData.COUNTRYNAME)&&(
                      <div style={{padding:"10px 14px",background:"#F8FAFC",
                        borderTop:"1px solid #F1F5F9"}}>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
                          {[
                            {label:"Anticipated launch", val:docData.LAUNCHDATE},
                            {label:"Market",             val:docData.COUNTRYNAME},
                            {label:"Launch assumptions", val:docData.LAUNCHASSUMPTIONS},
                          ].filter(r=>r.val).map((r,j)=>(
                            <div key={j}>
                              <div style={{fontSize:"10px",color:"#9CA3AF",marginBottom:"1px"}}>{r.label}</div>
                              <div style={{fontSize:"12px",color:"#374151",fontWeight:"500"}}>{r.val}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            );
          })()}
          {metaFields.length>0&&<div style={{background:"#fff",borderRadius:"12px",
            border:"1px solid #E5E7EB",padding:"14px 16px",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:"10px"}}>
              {metaFields.map((r,i)=>(
                <div key={i}>
                  <div style={{fontSize:"10px",color:"#9CA3AF",marginBottom:"2px"}}>{r.label}</div>
                  <div style={{fontSize:"12px",color:"#374151",fontWeight:"600"}}>{r.val}</div>
                </div>
              ))}
            </div>
          </div>}
          {/* Notes / Overview */}
          {docData.DOCTYPEID===98
            ? (docData.DESCRIPTION&&<div style={{background:"#fff",borderRadius:"12px",
                border:"1px solid #E5E7EB",overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
                <div style={{padding:"9px 14px",borderBottom:"1px solid #F1F5F9",background:"#F8FAFC",
                  fontSize:"11px",fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",letterSpacing:"0.6px"}}>
                  Overview
                </div>
                <div style={{padding:"14px 16px",fontSize:"13px",color:"#374151",lineHeight:1.75}}
                  dangerouslySetInnerHTML={{__html:docData.DESCRIPTION}}/>
              </div>)
            : ((docData.NOTES||docData.MESSAGES)&&<div style={{background:"#fff",borderRadius:"12px",
                border:"1px solid #E5E7EB",padding:"14px 16px",boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
                fontSize:"13px",color:"#374151",lineHeight:1.75}}
                dangerouslySetInnerHTML={{__html:docData.NOTES||docData.MESSAGES}}/>)
          }
          {/* Source */}
          {docData.LINK&&docData.LINK.trim()&&<SourceSection html={docData.LINK}/> }
        </div>
        {!isMobile&&<RightPanel files={files} images={images} chatbot={chatbot} user={user} activeSub={activeSub}
          documentId={docData.DOCUMENTID} docTitle={docData.SUBJECT||""} pollCount={docData.POLLCOUNT||0} commentCount={docData.COMMENTCOUNT||0}
          readOnly={readOnly} scrollToComment={scrollToComment||URL_COMMENT_ID}/>}
      </div>
      {/* Mobile drawer — Comments/Attachments/Polls */}
      {isMobile&&<MobileDrawer files={files} images={images} chatbot={chatbot} user={user} activeSub={activeSub}
        documentId={docData.DOCUMENTID} docTitle={docData.SUBJECT||""} pollCount={docData.POLLCOUNT||0} commentCount={docData.COMMENTCOUNT||0}
        readOnly={readOnly} scrollToComment={scrollToComment||URL_COMMENT_ID} tPrimary={tPrimary}/>}
    </div>
  );
}

// ─── Generic loader — uses advance_filter for all doctypes ───────────────────
function GenericLoader({ user, activeSub, docId, doctypeId, stateid="2", readOnly=false }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{
    if(!user||!activeSub||!docId) return;
    fetch(`https://cip.medsphere.net/api/advance_filter?subprojectid=${activeSub.SUBPROJECTID}&userid=${user.USERID}&doctypeid=${doctypeId}&documentid=${docId}`,
      { headers:{ "Auth-Code": user.AUTH_CODE } })
      .then(r=>r.json())
      .then(r=>{ if(r.STATUS==="1"&&Array.isArray(r.DATA)&&r.DATA.length>0) setData(r.DATA[0]); })
      .catch(()=>{}).finally(()=>setLoading(false));
  },[docId, activeSub?.SUBPROJECTID, doctypeId]);
  if(loading) return <Spin/>;
  if(!data)   return <div style={{display:"flex",alignItems:"center",justifyContent:"center",
    height:"100%",color:"#EF4444",fontSize:"13px"}}>Content not found</div>;
  return <GenericDetailView d={data} user={user} activeSub={activeSub} readOnly={readOnly} scrollToComment={URL_COMMENT_ID}/>;
}

// ─── Newsletter Share Modal (Users + Email tabs with select all) ──────────────
function NewsletterShareModal({ doc, user, activeSub, tPrimary, onClose }) {
  const [tab,      setTab]     = useState("users");
  const [users,    setUsers]   = useState([]);
  const [uLoad,    setULoad]   = useState(true);
  const [search,   setSearch]  = useState("");
  const [selected, setSelected]= useState([]);
  const [input,    setInput]   = useState("");
  const [emails,   setEmails]  = useState([]);
  const [sending,  setSending] = useState(false);
  const [result,   setResult]  = useState(null);

  useEffect(()=>{
    const fd = new FormData();
    fd.append("userid", user.USERID);
    fd.append("subprojectid", activeSub?.SUBPROJECTID);
    fetch(`https://cip.medsphere.net/api/DM_Users`,
      {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd})
      .then(r=>r.json())
      .then(res=>{ if(Array.isArray(res.DATA)) setUsers(res.DATA); })
      .catch(()=>{}).finally(()=>setULoad(false));
  },[]);

  const filtered = users.filter(u=>(u.NAME||"").toLowerCase().includes(search.toLowerCase()));
  const allSelected = filtered.length>0&&filtered.every(u=>selected.includes(u.USERID));

  const toggleUser = (uid) => setSelected(prev=>prev.includes(uid)?prev.filter(x=>x!==uid):[...prev,uid]);
  const toggleAll  = () => {
    const ids = filtered.map(u=>u.USERID);
    setSelected(allSelected ? selected.filter(id=>!ids.includes(id)) : [...new Set([...selected,...ids])]);
  };
  const addEmail = (raw) => {
    const vals = raw.split(/[,\s]+/).map(e=>e.trim().toLowerCase()).filter(e=>e.includes("@"));
    if(!vals.length) return;
    setEmails(prev=>[...new Set([...prev,...vals])]); setInput("");
  };
  const initials = (u) => { const p=(u.NAME||"").split(" "); return ((p[0]||"")[0]||(p[1]||"")[0]||"?").toUpperCase()+((p[1]||"")[0]||"").toUpperCase(); };
  const canSend = tab==="users"?selected.length>0:emails.length>0;

  const send = async () => {
    setSending(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("documentid",   doc.DOCUMENTID);
      fd.append("userid",       user.USERID);
      fd.append("subprojectid", activeSub?.SUBPROJECTID);
      if(tab==="users") selected.forEach(uid=>fd.append("receiver[]", uid));
      if(tab==="email") emails.forEach(e=>fd.append("email[]", e));
      const r = await fetch(`https://cip.medsphere.net/api/share_newsletter`,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res = await r.json();
      if(res.STATUS==="1"||res.STATUS===1){
        const count = tab==="users"?selected.length:emails.length;
        setResult({ok:true,msg:`Shared with ${count} recipient${count!==1?"s":""}`});
        setSelected([]); setEmails([]); setInput("");
      } else setResult({ok:false,msg:res.MESSAGE||"Share failed."});
    } catch { setResult({ok:false,msg:"Network error."}); }
    setSending(false);
  };

  return (
    <>
      <div onMouseDown={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:3000}}/>
      <div onMouseDown={e=>e.stopPropagation()}
        style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
          width:"min(480px,92vw)",maxHeight:"80vh",display:"flex",flexDirection:"column",
          background:"#fff",borderRadius:14,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",
          zIndex:3001,overflow:"hidden"}}>
        {/* Header */}
        <div style={{padding:"14px 18px",borderBottom:"1px solid #E5E7EB",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:15,fontWeight:700,color:"#111"}}>Share NewsFlash</div>
            <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:"#9CA3AF",lineHeight:1,padding:0}}>×</button>
          </div>
          <div style={{fontSize:12,color:"#6B7280",marginTop:2}}>{doc.SUBJECT}</div>
        </div>
        {/* Tabs */}
        <div style={{display:"flex",borderBottom:"1px solid #E5E7EB",flexShrink:0}}>
          {[{id:"users",label:"👤 Users"},{id:"email",label:"📧 Email"}].map(t=>(
            <button key={t.id} onClick={()=>{setTab(t.id);setResult(null);}}
              style={{flex:1,padding:"11px",border:"none",background:"none",cursor:"pointer",
                fontSize:13,fontWeight:tab===t.id?700:400,
                color:tab===t.id?tPrimary:"#6B7280",
                borderBottom:`2px solid ${tab===t.id?tPrimary:"transparent"}`}}>
              {t.label}
            </button>
          ))}
        </div>
        {/* Users tab */}
        {tab==="users"&&(
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{padding:"10px 14px",borderBottom:"1px solid #F3F4F6",flexShrink:0,display:"flex",alignItems:"center",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flex:1,background:"#F9FAFB",borderRadius:8,padding:"7px 12px",border:"1px solid #E5E7EB"}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input value={search} onChange={e=>setSearch(e.target.value)}
                  placeholder="Search users..."
                  style={{border:"none",outline:"none",background:"transparent",fontSize:13,flex:1}}/>
              </div>
              <div onClick={toggleAll} style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",flexShrink:0,fontSize:12,color:C.muted,userSelect:"none"}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${allSelected?tPrimary:"#D1D5DB"}`,background:allSelected?tPrimary:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {allSelected&&<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                All
              </div>
            </div>
            <div style={{flex:1,overflowY:"auto"}}>
              {uLoad&&<div style={{textAlign:"center",padding:"24px",color:"#9CA3AF",fontSize:13}}>Loading…</div>}
              {!uLoad&&filtered.map(u=>{
                const isSel = selected.includes(u.USERID);
                return (
                  <div key={u.USERID} onClick={()=>toggleUser(u.USERID)}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",cursor:"pointer",
                      background:isSel?"#F0F9FF":"transparent",borderBottom:"1px solid #F9FAFB"}}
                    onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background="#F9FAFB"; }}
                    onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.background="transparent"; }}>
                    <div style={{width:34,height:34,borderRadius:"50%",flexShrink:0,background:`${tPrimary}20`,color:tPrimary,fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {initials(u)}
                    </div>
                    <div style={{flex:1,fontSize:13,color:"#111",fontWeight:500}}>{u.NAME}</div>
                    <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${isSel?tPrimary:"#D1D5DB"}`,background:isSel?tPrimary:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {isSel&&<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Email tab */}
        {tab==="email"&&(
          <div style={{padding:"14px",flex:1}}>
            <div style={{fontSize:12,color:"#6B7280",marginBottom:8}}>Enter emails — press Enter or comma to add</div>
            <div style={{border:"1px solid #E5E7EB",borderRadius:8,padding:"8px 10px",display:"flex",flexWrap:"wrap",gap:4,minHeight:48,cursor:"text",marginBottom:8}}
              onClick={()=>document.getElementById("nflShareEmail")?.focus()}>
              {emails.map(e=>(
                <span key={e} style={{background:`${tPrimary}15`,color:tPrimary,fontSize:12,padding:"2px 8px",borderRadius:20,display:"flex",alignItems:"center",gap:4}}>
                  {e}<span onClick={ev=>{ev.stopPropagation();setEmails(prev=>prev.filter(x=>x!==e));}} style={{cursor:"pointer",opacity:0.6,fontSize:14}}>×</span>
                </span>
              ))}
              <input id="nflShareEmail" value={input}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"||e.key===","){e.preventDefault();addEmail(input);}}}
                onBlur={()=>input&&addEmail(input)}
                placeholder={emails.length?"":"name@company.com"}
                style={{border:"none",outline:"none",fontSize:13,flex:1,minWidth:140,background:"transparent"}}/>
            </div>
          </div>
        )}
        {result&&<div style={{padding:"0 16px 6px",fontSize:12,color:result.ok?"#16A34A":"#DC2626"}}>{result.msg}</div>}
        <div style={{padding:"10px 16px",borderTop:"1px solid #E5E7EB",display:"flex",justifyContent:"flex-end",gap:8,flexShrink:0}}>
          <button onClick={onClose} style={{padding:"8px 20px",borderRadius:8,border:"1px solid #E5E7EB",background:"#fff",color:"#6B7280",fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={send} disabled={sending||!canSend}
            style={{padding:"8px 24px",borderRadius:8,border:"none",background:tPrimary,color:"#fff",fontSize:13,fontWeight:600,cursor:sending||!canSend?"not-allowed":"pointer",opacity:sending||!canSend?0.6:1}}>
            {sending?"Sharing…":"Share"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── NewsFlash loader (dt=99) — uses newsletter API ──────────────────────────
export function NewsFlashLoader({ user, activeSub, docId, pid=null, stateid="2", readOnly=false }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [shareOpen, setShareOpen]= useState(false);
  const tPrimary = useContext(ThemeCtx)?.theme?.primary || C.primary;

  useEffect(()=>{
    if(!user||!activeSub||!docId) return;
    const params = {
      subprojectid: activeSub.SUBPROJECTID,
      userid:       user.USERID,
      documentid:   docId,
      stateid:      Number(stateid),
    };
    if(pid) params.parentid = pid;
    post("newsletter", params, user.AUTH_CODE)
      .then(r=>{
        if(r.STATUS==="1"){
          const doc = Array.isArray(r.DATA)?r.DATA[0]:(r.DATA||r);
          setData(doc);
        }
      })
      .catch(()=>{}).finally(()=>setLoading(false));
  },[docId, activeSub?.SUBPROJECTID]);

  if(loading) return <Spin/>;
  if(!data)   return <div style={{display:"flex",alignItems:"center",justifyContent:"center",
    height:"100%",color:"#EF4444",fontSize:"13px"}}>Not found</div>;

  if(editMode) return <InlineEdit doc={data} user={user} activeSub={activeSub}
    tPrimary={tPrimary} onDone={()=>setEditMode(false)} onCancel={()=>setEditMode(false)}/>;

  const doc      = data;
  const relative = Array.isArray(data.RELATIVE)?data.RELATIVE:[];
  const chatbot  = Array.isArray(data.CHATBOT)?data.CHATBOT:[];
  const images   = chatbot.filter(a=>isImg(a.FILEPATH)).sort((a,b)=>a.ATTACHMENTID-b.ATTACHMENTID);
  const files    = chatbot.filter(a=>!isImg(a.FILEPATH)).sort((a,b)=>b.ATTACHMENTID-a.ATTACHMENTID);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {shareOpen&&<NewsletterShareModal doc={data} user={user} activeSub={activeSub}
        tPrimary={tPrimary} onClose={()=>setShareOpen(false)}/>}
      {/* Hero */}
      <div style={{flexShrink:0,padding:"16px 20px",
        background:`linear-gradient(135deg,${tPrimary}F0,${tPrimary}88)`,
        position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",right:"-20px",top:"-20px",width:"130px",height:"130px",
          borderRadius:"50%",background:"rgba(255,255,255,0.05)"}}/>
        {/* Share button */}
        <button onClick={()=>setShareOpen(true)}
          style={{position:"absolute",top:12,right:12,zIndex:10,
            display:"flex",alignItems:"center",gap:5,
            padding:"4px 11px",borderRadius:20,
            background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.35)",
            color:"#fff",fontSize:11,fontWeight:600,cursor:"pointer"}}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/>
            <circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          Share
        </button>
        <div style={{display:"flex",gap:"12px",alignItems:"flex-start",position:"relative"}}>
          {data.LOGO&&<div style={{width:"44px",height:"44px",borderRadius:"10px",flexShrink:0,
            background:"rgba(255,255,255,0.18)",border:"1.5px solid rgba(255,255,255,0.3)",
            overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <img src={data.LOGO} alt="" onError={e=>e.target.style.display="none"}
              style={{width:"80%",height:"80%",objectFit:"contain"}}/>
          </div>}
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"inline-flex",background:"rgba(255,255,255,0.2)",
              borderRadius:"20px",padding:"1px 8px",marginBottom:"4px",fontSize:"9px",
              fontWeight:"700",color:"rgba(255,255,255,0.9)",textTransform:"uppercase",
              letterSpacing:"0.6px"}}>NewsFlash</div>
            <div style={{fontSize:"17px",fontWeight:"800",color:"#fff",lineHeight:1.25,marginBottom:"4px"}}>
              {data.SUBJECT}
            </div>
            <div style={{display:"flex",gap:"6px",flexWrap:"wrap",alignItems:"center"}}>
              {data.DISPLAY_TIME&&<span style={{fontSize:"10px",color:"rgba(255,255,255,0.7)",
                background:"rgba(255,255,255,0.15)",borderRadius:"5px",padding:"1px 7px"}}>
                📅 {data.DISPLAY_TIME}</span>}
              {data.FIRSTNAME&&<span style={{fontSize:"11px",color:"rgba(255,255,255,0.8)",fontWeight:"600"}}>
                {data.FIRSTNAME}</span>}
            </div>
          </div>
          {images.length>0&&<div style={{width:"56px",height:"56px",borderRadius:"10px",flexShrink:0,
            overflow:"hidden",border:"2px solid rgba(255,255,255,0.3)",background:"rgba(255,255,255,0.1)"}}>
            <img src={thumbUrl(images[0].FILEPATH)} alt="" onError={e=>e.target.style.display="none"}
              style={{width:"100%",height:"100%",objectFit:"contain"}}/>
          </div>}
        </div>
      </div>
      <AdminBar doc={data} user={user} activeSub={activeSub} tPrimary={tPrimary}
        onEdit={()=>setEditMode(true)}/>
      {/* Body */}
      <div style={{flex:1,display:"flex",overflow:"hidden",minHeight:0}}>
        <div style={{flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:"12px"}}>
          {/* Notes */}
          {data.NOTES&&<div style={{background:"#fff",borderRadius:"12px",border:"1px solid #E5E7EB",
            padding:"14px 16px",boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
            fontSize:"13px",color:"#374151",lineHeight:1.75}}
            dangerouslySetInnerHTML={{__html:data.NOTES}}/>}
          {/* Related documents */}
          {relative.length>0&&<div style={{background:"#fff",borderRadius:"12px",
            border:"1px solid #E5E7EB",overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
            <div style={{padding:"8px 14px",background:"#F8FAFC",borderBottom:"1px solid #F1F5F9",
              fontSize:"11px",fontWeight:"700",color:"#374151",textTransform:"uppercase",
              letterSpacing:"0.5px"}}>Related documents</div>
            {relative.map((r,i)=>(
              <div key={i} onClick={()=>openDocTab({DOCUMENTID:r.DOCUMENTID,DOCTYPEID:r.DOCTYPEID},activeSub?.SUBPROJECTID)}
                style={{display:"flex",alignItems:"center",gap:"10px",padding:"9px 14px",
                  cursor:"pointer",borderBottom:i<relative.length-1?"1px solid #F1F5F9":"none",
                  transition:"background 0.1s"}}
                onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{width:"6px",height:"6px",borderRadius:"50%",flexShrink:0,
                  background:tPrimary}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:"12px",fontWeight:"600",color:"#111827",
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.SUBJECT}</div>
                  {r.DISPLAY_TIME&&<div style={{fontSize:"10px",color:"#9CA3AF"}}>{r.DISPLAY_TIME}</div>}
                </div>
                {r.DOCTYPENAME&&<span style={{fontSize:"10px",padding:"1px 6px",
                  background:`${tPrimary}10`,color:tPrimary,borderRadius:"4px",flexShrink:0}}>
                  {r.DOCTYPENAME}</span>}
              </div>
            ))}
          </div>}
          {/* Source */}
          {data.LINK&&data.LINK.trim()&&<SourceSection html={data.LINK}/>}
        </div>
        <RightPanel files={files} images={images} chatbot={chatbot} user={user} activeSub={activeSub}
          documentId={data.DOCUMENTID} docTitle={data.SUBJECT||""} pollCount={data.POLLCOUNT||0} commentCount={data.COMMENTCOUNT||0}
          readOnly={readOnly}/>
      </div>
    </div>
  );
}

// ─── Workshop loader (dt=100) — uses workshop_list API ────────────────────────
function WorkshopLoader({ user, activeSub, docId, stateid="2", readOnly=false }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const tPrimary = useContext(ThemeCtx)?.theme?.primary || C.primary;

  useEffect(()=>{
    if(!user||!activeSub||!docId) return;
    post("workshop_list", {
      subprojectid: activeSub.SUBPROJECTID,
      userid:       user.USERID,
      stateid:      Number(stateid),
      documentid:   docId,
    }, user.AUTH_CODE)
      .then(r=>{
        if(r.STATUS==="1"){
          const doc = Array.isArray(r.DATA)?r.DATA[0]:(r.DATA||r);
          setData(doc);
        }
      })
      .catch(()=>{}).finally(()=>setLoading(false));
  },[docId, activeSub?.SUBPROJECTID]);

  if(loading) return <Spin/>;
  if(!data)   return <div style={{display:"flex",alignItems:"center",justifyContent:"center",
    height:"100%",color:"#EF4444",fontSize:"13px"}}>Not found</div>;

  if(editMode) return <InlineEdit doc={data} user={user} activeSub={activeSub}
    tPrimary={tPrimary} onDone={()=>setEditMode(false)} onCancel={()=>setEditMode(false)}/>;

  const chatbot  = Array.isArray(data.CHATBOT)?data.CHATBOT:[];
  const images   = chatbot.filter(a=>isImg(a.FILEPATH)).sort((a,b)=>a.ATTACHMENTID-b.ATTACHMENTID);
  const files    = chatbot.filter(a=>!isImg(a.FILEPATH)).sort((a,b)=>b.ATTACHMENTID-a.ATTACHMENTID);
  const details  = Array.isArray(data.WORKSHOP_DETAIL)?data.WORKSHOP_DETAIL:[];
  const ACCENTS  = ["#2563EB","#059669","#7C3AED","#D97706","#DB2777","#0891B2"];

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Hero */}
      <div style={{flexShrink:0,padding:"16px 20px",
        background:`linear-gradient(135deg,${tPrimary}F0,${tPrimary}88)`,
        position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",right:"-20px",top:"-20px",width:"130px",height:"130px",
          borderRadius:"50%",background:"rgba(255,255,255,0.05)"}}/>
        <div style={{display:"flex",gap:"12px",alignItems:"flex-start",position:"relative"}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"inline-flex",background:"rgba(255,255,255,0.2)",
              borderRadius:"20px",padding:"1px 8px",marginBottom:"4px",fontSize:"9px",
              fontWeight:"700",color:"rgba(255,255,255,0.9)",textTransform:"uppercase",
              letterSpacing:"0.6px"}}>Workshop</div>
            <div style={{fontSize:"17px",fontWeight:"800",color:"#fff",lineHeight:1.25,marginBottom:"4px"}}>
              {data.SUBJECT}
            </div>
            <div style={{display:"flex",gap:"6px",flexWrap:"wrap",alignItems:"center"}}>
              {data.DISPLAY_TIME&&<span style={{fontSize:"10px",color:"rgba(255,255,255,0.7)",
                background:"rgba(255,255,255,0.15)",borderRadius:"5px",padding:"1px 7px"}}>
                📅 {data.DISPLAY_TIME}</span>}
              {details.length>0&&<span style={{fontSize:"10px",color:"rgba(255,255,255,0.8)",
                background:"rgba(255,255,255,0.15)",borderRadius:"5px",padding:"1px 7px"}}>
                {details.length} scenario{details.length!==1?"s":""}</span>}
            </div>
          </div>
          {images.length>0&&<div style={{width:"56px",height:"56px",borderRadius:"10px",flexShrink:0,
            overflow:"hidden",border:"2px solid rgba(255,255,255,0.3)",background:"rgba(255,255,255,0.1)"}}>
            <img src={thumbUrl(images[0].FILEPATH)} alt="" onError={e=>e.target.style.display="none"}
              style={{width:"100%",height:"100%",objectFit:"contain"}}/>
          </div>}
        </div>
      </div>

      <AdminBar doc={data} user={user} activeSub={activeSub} tPrimary={tPrimary}
        onEdit={()=>setEditMode(true)}/>
      {/* Body */}
      <div style={{flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:"12px"}}>
        {/* Top-level notes */}
        {data.NOTES&&<div style={{background:"#fff",borderRadius:"12px",border:"1px solid #E5E7EB",
          padding:"14px 16px",boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
          fontSize:"13px",color:"#374151",lineHeight:1.75}}
          dangerouslySetInnerHTML={{__html:data.NOTES}}/>}

        {/* Scenario cards — same style as left menu */}
        {details.map((d,i)=>{
          const accent = ACCENTS[i%ACCENTS.length];
          const dChatbot = Array.isArray(d.CHATBOT)?d.CHATBOT:[];
          const dImages  = dChatbot.filter(a=>isImg(a.FILEPATH)).sort((a,b)=>a.ATTACHMENTID-b.ATTACHMENTID);
          const dFiles   = dChatbot.filter(a=>!isImg(a.FILEPATH)).sort((a,b)=>b.ATTACHMENTID-a.ATTACHMENTID);
          const cos      = Array.isArray(d.COMPANY)?d.COMPANY:[];
          const co       = cos.find(c=>c.PRIMARY===1)||cos[0]||null;
          const logoUrl  = co?.COMPANY_LOGO?LOGO_BASE+co.COMPANY_LOGO:null;
          return (
            <div key={i} style={{background:"#fff",borderRadius:"12px",overflow:"hidden",
              border:`1px solid #E5E7EB`,boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
              <div style={{height:"3px",background:accent}}/>
              {/* Scenario header */}
              <div style={{padding:"12px 14px",display:"flex",alignItems:"flex-start",gap:"10px"}}>
                <div style={{width:"28px",height:"28px",borderRadius:"6px",flexShrink:0,
                  background:`${accent}18`,border:`1px solid ${accent}33`,
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <span style={{fontSize:"11px",fontWeight:"900",color:accent}}>{i+1}</span>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:"13px",fontWeight:"700",color:"#111827",
                    lineHeight:1.3,marginBottom:"4px"}}>
                    {d.TITLE||d.BRANDNAME||"Untitled"}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:"6px",flexWrap:"wrap"}}>
                    {logoUrl&&<img src={logoUrl} alt="" onError={e=>e.target.style.display="none"}
                      style={{width:"14px",height:"14px",objectFit:"contain",borderRadius:"2px"}}/>}
                    {d.SPONSORNAME&&<span style={{fontSize:"10px",color:"#6B7280"}}>{d.SPONSORNAME}</span>}
                    {d.BRANDNAME&&d.BRANDNAME!=="Other"&&<span style={{fontSize:"10px",fontWeight:"600",
                      color:accent,background:`${accent}12`,borderRadius:"4px",padding:"1px 6px"}}>
                      {d.BRANDNAME}</span>}
                    {(d.PHASE||d.PHASENAME)&&(()=>{
                      const pc=phaseCfg(d.PHASE||d.PHASENAME);
                      return <span style={{fontSize:"9px",fontWeight:"700",padding:"1px 5px",
                        borderRadius:"4px",color:pc.color,background:pc.bg}}>
                        {d.PHASE||d.PHASENAME}</span>;
                    })()}
                    {d.LAUNCHDATE&&<span style={{fontSize:"9px",color:"#9CA3AF"}}>· 🚀 {d.LAUNCHDATE}</span>}
                    {d.INDICATIONNAME&&<span style={{fontSize:"9px",color:"#9CA3AF"}}>· {d.INDICATIONNAME}</span>}
                  </div>
                </div>
              </div>
              {/* Scenario body */}
              <div style={{padding:"0 14px 14px",borderTop:"1px solid #F1F5F9"}}>
                {/* Images */}
                {dImages.length>0&&<div style={{marginTop:"10px",marginBottom:"10px"}}>
                  <div style={{borderRadius:"8px",overflow:"hidden",background:"#F1F5F9",
                    border:"1px solid #E5E7EB",height:"200px",marginBottom:"6px",
                    display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-in"}}
                    onClick={()=>window.open(attachUrl(dImages[0].FILEPATH),"_blank","noopener,noreferrer")}>
                    <img src={attachUrl(dImages[0].FILEPATH)} alt=""
                      onError={e=>e.target.style.display="none"}
                      style={{maxWidth:"100%",maxHeight:"200px",objectFit:"contain"}}/>
                  </div>
                  {dImages.length>1&&<div style={{display:"flex",gap:"4px",overflowX:"auto",scrollbarWidth:"none"}}>
                    {dImages.map((img,j)=>(
                      <div key={j} onClick={()=>window.open(attachUrl(img.FILEPATH),"_blank","noopener,noreferrer")}
                        style={{flexShrink:0,width:"44px",height:"44px",borderRadius:"5px",
                          overflow:"hidden",cursor:"pointer",border:`2px solid ${accent}44`}}>
                        <img src={thumbUrl(img.FILEPATH)} alt="" onError={e=>e.target.style.display="none"}
                          style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      </div>
                    ))}
                  </div>}
                </div>}
                {/* Messages */}
                {d.MESSAGES&&<div style={{fontSize:"13px",color:"#374151",lineHeight:1.75,marginTop:"10px"}}
                  dangerouslySetInnerHTML={{__html:d.MESSAGES}}/>}
                {/* Files */}
                {dFiles.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:"6px",marginTop:"10px"}}>
                  {dFiles.map((f,j)=><FilePillSmall key={j} filepath={f.FILEPATH}/>)}
                </div>}
              </div>
            </div>
          );
        })}

        {data.LINK&&data.LINK.trim()&&<SourceSection html={data.LINK}/>}
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
const URL_COMMENT_ID = new URLSearchParams(window.location.search).get("commentid");


// ─── Product profile loader (dt=97) ──────────────────────────────────────────
function ProfileLoader({ user, activeSub, docId }) {
  const [product,  setProduct]  = useState(null);
  const [advDoc,   setAdvDoc]   = useState(null);
  const [editMode, setEditMode] = useState(false);
  const tPrimary = useContext(ThemeCtx)?.theme?.primary || C.primary;
  const isAdmin  = activeSub?.ROLE === 1;

  useEffect(()=>{
    if(!user||!activeSub||!docId) return;
    fetch(`https://cip.medsphere.net/api/product_profile_list?subprojectid=${activeSub.SUBPROJECTID}&userid=${user.USERID}&documentid=${docId}`,
      { headers:{ "Auth-Code": user.AUTH_CODE } })
      .then(r=>r.json())
      .then(r=>{ if(r.STATUS==="1"&&Array.isArray(r.DATA)&&r.DATA.length>0) setProduct(r.DATA[0]); })
      .catch(()=>{});
    fetch(`https://cip.medsphere.net/api/advance_filter?documentid=${docId}&subprojectid=${activeSub.SUBPROJECTID}&userid=${user.USERID}&doctypeid=97`,
      { headers:{ "Auth-Code": user.AUTH_CODE } })
      .then(r=>r.json())
      .then(r=>{ if((r.STATUS==="1"||r.STATUS===1)&&r.DATA?.length>0) setAdvDoc(r.DATA[0]); })
      .catch(()=>{});
  },[docId, activeSub?.SUBPROJECTID]);

  if(editMode&&advDoc) return(
    <InlineEdit doc={advDoc} user={user} activeSub={activeSub}
      tPrimary={tPrimary} onDone={()=>setEditMode(false)}/>
  );

  const adminBarEl = advDoc ? (
    <AdminBar doc={advDoc} user={user} activeSub={activeSub} tPrimary={tPrimary}
      onEdit={isAdmin?()=>setEditMode(true):undefined}/>
  ) : null;

  return <ProductProfileDetail product={product} adminBar={adminBarEl}/>;
}

export default function DocDetailTab({ session, onLogout, docId, doctypeId }) {
  const user      = session?.user || null;
  const activeSub = session?.subproject || null;
  if (!user) return null;

  // Read stateid from localStorage (set by opener) — admin may open inactive docs
  const isAdmin  = activeSub?.ROLE === 1;
  const stateid  = isAdmin ? (localStorage.getItem(`doc_stateid_${docId}`) || "2") : "2";
  const readOnly = stateid === "1";

  let content;
  if (doctypeId===97) {
    content = <ProfileLoader user={user} activeSub={activeSub} docId={docId}/>;
  } else if (doctypeId===99) {
    content = <NewsFlashLoader user={user} activeSub={activeSub} docId={docId} stateid={stateid} readOnly={readOnly}/>;
  } else if (doctypeId===100) {
    content = <WorkshopLoader user={user} activeSub={activeSub} docId={docId} stateid={stateid} readOnly={readOnly}/>;
  } else {
    // All other doctypes including 97 (Profile), 98 (Product Detail), 89 (Echo) etc.
    // use advance_filter via GenericLoader
    content = <GenericLoader user={user} activeSub={activeSub} docId={docId} doctypeId={doctypeId} stateid={stateid} readOnly={readOnly}/>;
  }

  return (
    <AppShell session={session} onLogout={onLogout} overrideContent={content} hideNav={true} hideFilterButtons={true}/>
  );
}